/**
 * email-capture.js
 *
 * Handles both Klaviyo-backed signup forms — the footer newsletter and the
 * password page's early-access capture. Which list a form subscribes to comes
 * from `data-list` on its wrapper, so the two share one implementation.
 *
 * Every form is a real Shopify `customer` form underneath. If this module never
 * loads, or Klaviyo errors, the native submit runs and the address lands in
 * Shopify's customer list instead. An email is never silently dropped.
 */

const config = window.AfterthreadKlaviyo;
const ENDPOINT = 'https://a.klaviyo.com/client/subscriptions/';

function listFor(name) {
  if (!config) return null;
  return name === 'early-access' ? config.earlyAccessList : config.newsletterList;
}

async function subscribe(email, listId, firstName) {
  const attributes = { email };
  if (firstName) attributes.first_name = firstName;

  const response = await fetch(`${ENDPOINT}?company_id=${encodeURIComponent(config.companyId)}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      revision: '2024-10-15'
    },
    body: JSON.stringify({
      data: {
        type: 'subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes
            }
          }
        },
        relationships: {
          list: { data: { type: 'list', id: listId } }
        }
      }
    })
  });

  if (!response.ok) throw new Error(`Klaviyo responded ${response.status}`);
}

/**
 * Files the same signup in Shopify, so an address exists in both systems.
 *
 * Klaviyo's Shopify integration syncs customers *out of* Shopify, not back
 * into it — so without this a Klaviyo subscriber would never appear in your
 * Shopify customer list.
 *
 * Deliberately non-fatal: by the time this runs Klaviyo already has the
 * address, so a failure here must not surface as a failed signup.
 */
async function alsoSaveToShopify(form) {
  try {
    const body = new URLSearchParams(new FormData(form));
    await fetch(form.action || '/contact', {
      method: 'POST',
      body,
      headers: { Accept: 'text/html' }
    });
  } catch (error) {
    /* Klaviyo has it; Shopify can be reconciled later. */
  }
}

function showSuccess(wrapper, form) {
  const message = document.createElement('p');
  message.className = wrapper.dataset.successClass || 'at-newsletter__success';
  message.setAttribute('role', 'status');
  message.textContent = wrapper.dataset.successMessage || '';
  form.replaceChildren(message);
}

function init() {
  if (!config || !config.companyId) return;

  document.querySelectorAll('[data-newsletter]').forEach((wrapper) => {
    const form = wrapper.querySelector('form');
    if (!form || form.dataset.captureReady) return;

    const listId = listFor(wrapper.dataset.list);
    if (!listId) return; // No list configured — leave the native form alone.

    form.dataset.captureReady = 'true';

    form.addEventListener('submit', async (event) => {
      const input = form.querySelector('input[type="email"]');
      if (!input || !input.value.includes('@')) return;

      event.preventDefault();

      const submit = form.querySelector('button[type="submit"]');
      if (submit) submit.disabled = true;

      try {
        const firstName = form.querySelector('input[name="contact[first_name]"]')?.value.trim();
        await subscribe(input.value.trim(), listId, firstName);
        // Klaviyo first — it is the destination that matters most here. Only
        // once it has succeeded do we mirror the signup into Shopify.
        await alsoSaveToShopify(form);
        showSuccess(wrapper, form);
      } catch (error) {
        // Hand back to Shopify's native submit rather than losing the address.
        if (submit) submit.disabled = false;
        form.dataset.captureReady = '';
        form.submit();
      }
    });
  });
}

init();
document.addEventListener('shopify:section:load', init);
